import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useContact } from "@/context/ContactContext";
import { BRAND } from "@/data/brand";
import { linkifyDieselPaco } from "@/components/DieselPacoText";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -60 });
  else el?.scrollIntoView({ behavior: "smooth" });
};

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.35 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero() {
  const { t } = useLang();
  const { openContact } = useContact();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  const lines = [t.hero.line1, t.hero.line2, t.hero.line3];

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={BRAND.hero}
          alt=""
          className="h-full w-full object-cover object-center"
        />
        <motion.div
          style={{ opacity: overlay }}
          className="absolute inset-0 bg-black"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1712] via-[#0B1712]/30 to-transparent" />
        <div className="hero-grain absolute inset-0" />
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-[1400px] px-5 sm:px-8 pb-14 sm:pb-20 pt-28">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="font-mono text-xs sm:text-sm tracking-[0.25em] uppercase text-[color:var(--dp-orange)] mb-5"
        >
          {t.hero.overline}
        </motion.p>

        <h1 className="font-heading font-black uppercase tracking-tighter text-white leading-[0.92] text-[12vw] sm:text-[9vw] lg:text-[6.4vw]">
          {lines.map((ln, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={lineReveal}
                initial="hidden"
                animate="show"
                className="block"
              >
                {ln}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mt-7 max-w-xl text-base sm:text-lg text-white/70 font-light leading-relaxed"
        >
          {linkifyDieselPaco(t.hero.sub)}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.8 }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-catalog-cta"
            onClick={() => scrollTo("catalog")}
            className="group inline-flex items-center gap-2 px-7 py-3.5 bg-[color:var(--dp-orange)] hover:bg-[color:var(--dp-orange-hover)] text-black font-semibold transition-colors"
          >
            {t.hero.cta}
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <button
            data-testid="hero-contact-cta"
            onClick={() => openContact(null)}
            className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/25 text-white font-semibold hover:bg-white/10 transition-colors"
          >
            {t.hero.cta2}
          </button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 right-6 sm:right-8 flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-white/50"
      >
        {t.hero.scroll}
        <ArrowDown size={14} className="animate-bounce" />
      </motion.div>
    </section>
  );
}
